import React, { useState } from 'react';
import config from '../config/environment';

const CreateRoom = ({ onNavigate }) => {
  const [formData, setFormData] = useState({
    roomId: '',
    playerName: '',
    boardSize: 15,
    winCondition: 5,
    timeControl: 'unlimited',
    customTime: 600,
    isPrivate: false,
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const timeControlOptions = {
    unlimited: { time: 0, label: 'Unlimited' },
    blitz: { time: 300, label: 'Blitz (5 min)' },
    rapid: { time: 600, label: 'Rapid (10 min)' },
    long: { time: 1800, label: 'Long (30 min)' },
    custom: { time: 600, label: 'Custom' }
  };

  const boardSizeOptions = [
    { value: 15, label: '15×15 (Standard)' },
    { value: 19, label: '19×19 (Large)' },
    { value: 13, label: '13×13 (Medium)' },
    { value: 11, label: '11×11 (Small)' }
  ];

  const winConditionOptions = [
    { value: 5, label: '5 in a row (Standard)' },
    { value: 4, label: '4 in a row' },
    { value: 6, label: '6 in a row' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const generateRoomId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, roomId: result }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!formData.roomId || !formData.playerName) {
        throw new Error('Room ID and player name are required');
      }

      const timeControl = formData.timeControl === 'custom' ? 
        { type: 'custom', time: formData.customTime } :
        timeControlOptions[formData.timeControl];

      const response = await fetch(`${config.getApiUrl()}/api/room/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          roomId: formData.roomId,
          playerName: formData.playerName,
          boardSize: parseInt(formData.boardSize),
          winCondition: parseInt(formData.winCondition),
          timeControl: timeControl,
          isPrivate: formData.isPrivate,
          password: formData.password
        })
      });

      const result = await response.json();

      if (result.success) {
        onNavigate('game', {
          roomId: formData.roomId,
          playerName: formData.playerName,
          isCreator: true
        });
      } else {
        setError(result.error || 'Failed to create room');
      }
    } catch (err) {
      setError(err.message || 'Failed to create room');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="card">
        <h2>Create Caro Room</h2>
        
        <form onSubmit={handleSubmit} className="form-container">
          <div className="form-group">
            <label htmlFor="roomId">Room ID</label>
            <div className="input-with-button">
              <input
                type="text"
                id="roomId"
                name="roomId"
                value={formData.roomId}
                onChange={handleInputChange}
                placeholder="Enter room ID"
                required
              />
              <button 
                type="button" 
                onClick={generateRoomId}
                className="secondary-button"
              >
                Generate
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="playerName">Your Name</label>
            <input
              type="text"
              id="playerName"
              name="playerName"
              value={formData.playerName}
              onChange={handleInputChange}
              placeholder="Enter your name"
              maxLength={20}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="boardSize">Board Size</label>
              <select
                id="boardSize"
                name="boardSize"
                value={formData.boardSize}
                onChange={handleInputChange}
              >
                {boardSizeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="winCondition">Win Condition</label>
              <select
                id="winCondition"
                name="winCondition"
                value={formData.winCondition}
                onChange={handleInputChange}
              >
                {winConditionOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="timeControl">Time Control</label>
            <select
              id="timeControl"
              name="timeControl"
              value={formData.timeControl}
              onChange={handleInputChange}
            >
              {Object.entries(timeControlOptions).map(([key, option]) => (
                <option key={key} value={key}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {formData.timeControl === 'custom' && (
            <div className="form-group">
              <label htmlFor="customTime">Custom Time (seconds)</label>
              <input
                type="number"
                id="customTime"
                name="customTime"
                value={formData.customTime}
                onChange={handleInputChange}
                min="60"
                max="7200"
              />
            </div>
          )}

          <div className="checkbox-group">
            <label>
              <input
                type="checkbox"
                name="isPrivate"
                checked={formData.isPrivate}
                onChange={handleInputChange}
              />
              Private room
            </label>
          </div>

          {formData.isPrivate && (
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter room password"
              />
            </div>
          )}

          {error && <div className="error">{error}</div>}

          <div className="form-actions">
            <button
              type="button"
              className="secondary-button"
              onClick={() => onNavigate('home')}
            >
              Back
            </button>
            <button
              type="submit"
              className="primary-button"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Room'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateRoom;
